/*eslint max-lines-per-function: ["error", 240]*/
import React, { ReactNode, useState } from 'react';
import { Alert, Table } from 'react-bootstrap';
import moment from 'moment';
import type { TFunction } from 'i18next';

import mathUtils from '../../../utils/mathUtils';
import {
  CreateUpdateInvoiceEstimateItemDto,
  GetEstimateItemDto,
  GetProjectDto,
  GetSalesInvoiceDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import numberFormatter from '../../../common/numberFormatter';
import CurrencyControl from '../../../app/components/CurrencyControl';
import momencik from '../../../common/momencik';
import { SalesInvoiceStatusEnum } from '../../../common/pokCore/validation/enums';
import {
  RangeFilter,
  InvoiceEstimateItemsFilters,
} from '../Invoices/InvoiceEstimateItemsFilters';
import { CopyAllButton, CopySingleButton } from '../Invoices/CopyAmountButtons';

import {
  calculateEstimateItemSalesNet,
  calculateEstimateItemCommission,
} from './calculateSalesInvoiceAmountsUtils';
import SalesInvoiceMediaPlanPositionsSumRow from './SalesInvoiceMediaPlanPositionsSumRow';

interface SalesInvoiceMediaplanPositionsGridProps {
  readOnly?: boolean;
  availableEstimateItems?: GetEstimateItemDto[];
  salesInvoice?: GetSalesInvoiceDto;
  salesInvoiceEstimateItems?: CreateUpdateInvoiceEstimateItemDto[];
  salesInvoiceOriginalEstimateItems?: CreateUpdateInvoiceEstimateItemDto[];
  parentAmount?: string;
  projects: GetProjectDto[];
  propertyChange: (
    obj: Partial<CreateUpdateInvoiceEstimateItemDto>,
    id: string,
  ) => void;
  propertyChangeMultiple?: (
    changes: { obj: Partial<CreateUpdateInvoiceEstimateItemDto>; id: string }[],
  ) => void;
}

export interface TableColumn {
  header: string | ReactNode;
  key: string;
  tdCssClass?: string;
  isHidden?: boolean;
  renderCell: (
    item: GetEstimateItemDto,
    sales: number,
    commission: number,
  ) => string | ReactNode | null;
}

export const getFinancialAccountName = (
  year: number,
  internal: boolean,
  item: GetEstimateItemDto,
) =>
  `${item.profileCategory?.name} ${
    item.profileCategory.profileCategoriesYears?.filter(
      o =>
        o.year === year &&
        (o.internal === internal ||
          o.internal === null ||
          o.internal === undefined) &&
        o.active === true,
    )[0].commissionFinancialAccount !==
    item.profileCategory.profileCategoriesYears?.filter(
      o =>
        o.year === year &&
        (o.internal === internal ||
          o.internal === null ||
          o.internal === undefined) &&
        o.active === true,
    )[0].salesFinancialAccount
      ? '(pr.)'
      : ''
  }`;
const getColumns = (
  props: SalesInvoiceMediaplanPositionsGridProps,
  isGenerated: boolean,
  t: TFunction<'fvsLng', 'fvsLng'>,
  tk: TKeys<'fvsLng'>,
  handleFillAllAmounts?: () => void,
  handleFillAllCommissions?: () => void,
  allAmountsFilled?: boolean,
  allCommissionsFilled?: boolean,
): TableColumn[] => {
  const isCorrection = !!props.salesInvoice?.parent;

  const getAmount = (item: GetEstimateItemDto) =>
    props.salesInvoiceEstimateItems?.find(
      ({ estimateItemId }) => estimateItemId === item.id,
    )?.amount || '';

  const getCommission = (item: GetEstimateItemDto) =>
    props.salesInvoiceEstimateItems?.find(
      ({ estimateItemId }) => estimateItemId === item.id,
    )?.commissionAmount || '';

  const getOriginalAmount = (item: GetEstimateItemDto) =>
    props.salesInvoiceOriginalEstimateItems?.find(
      ({ estimateItemId }) => estimateItemId === item.id,
    )?.amount || '0';

  const getOriginalCommissionAmount = (item: GetEstimateItemDto) =>
    props.salesInvoiceOriginalEstimateItems?.find(
      ({ estimateItemId }) => estimateItemId === item.id,
    )?.commissionAmount || '0';

  const getPreviousAmount = (item: GetEstimateItemDto) =>
    props.salesInvoice?.parent.invoiceEstimateItems?.find(
      ({ estimateItem }) => estimateItem.id === item.id,
    )?.amount || '0';

  const getPreviousCommission = (item: GetEstimateItemDto) =>
    props.salesInvoice?.parent.invoiceEstimateItems?.find(
      ({ estimateItem }) => estimateItem.id === item.id,
    )?.commissionAmount || '0';

  const onChangeAmount = (value: string | null, id: string) =>
    props.propertyChange(
      {
        amount: value || '',
      },
      id,
    );

  const onChangeCommission = (value: string | null, id: string) =>
    props.propertyChange(
      {
        commissionAmount: value || '',
      },
      id,
    );

  return [
    {
      key: 'project',
      header: t(tk.prLang.projectNumber),
      renderCell: item => item.estimateByMonth?.project?.number,
    },
    {
      key: 'name',
      header: t(tk.prLang.estimateItemPosition),
      renderCell: item => item.position.name,
    },
    {
      key: 'month',
      header: t(tk.common.month),
      renderCell: item => momencik(item.estimateByMonth?.date, 'YYYY-MM'),
    },
    {
      key: 'financial-account',
      header: t(tk.finance.financialAccount),
      renderCell: item => item.profileCategory?.name,
    },
    {
      key: 'sale',
      header: t(tk.common.sales),
      renderCell: (item, sales) => numberFormatter(sales, true),
      isHidden: isGenerated,
      tdCssClass: 'amounts-align-right',
    },
    {
      key: 'sale-amount',
      header: t(tk.common.amount),
      renderCell: (item, sales) => (
        <CurrencyControl
          value={getAmount(item)}
          disabled={
            (sales === 0 && getOriginalAmount(item) === '0') || props.readOnly
          }
          decimalScale={2}
          onChange={value => onChangeAmount(value, item.id)}
        />
      ),
      isHidden: isCorrection,
    },
    {
      key: 'sale-amount-previous',
      header: t(tk.finance.was),
      renderCell: item => numberFormatter(getPreviousAmount(item), true),
      isHidden: !isCorrection,
      tdCssClass: 'amounts-align-right',
    },
    {
      key: 'amount-copy',
      header: (
        <CopyAllButton
          onClick={() => handleFillAllAmounts?.()}
          tooltip={t(tk.finance.fillAllFieldsWithSales)}
          disabled={props.readOnly}
          allFilled={allAmountsFilled}
        />
      ),
      renderCell: (item, sales) => (
        <CopySingleButton
          onClick={() =>
            onChangeAmount(sales > 0 ? sales.toString() : '0', item.id)
          }
          tooltip={t(tk.finance.fillNotAssignedAmountWithSales)}
          disabled={props.readOnly}
          hidden={
            mathUtils.subtract(getAmount(item), sales) === 0 ||
            (sales < 0 && mathUtils.parse(getAmount(item)) === 0)
          }
        />
      ),
      isHidden: !isCorrection,
    },
    {
      key: 'sale-amount-correction',
      header: t(tk.finance.is),
      renderCell: (item, sales) => (
        <CurrencyControl
          value={getAmount(item)}
          disabled={
            (sales === 0 && getPreviousAmount(item) === '0') || props.readOnly
          }
          decimalScale={2}
          onChange={value => onChangeAmount(value, item.id)}
        />
      ),
      isHidden: !isCorrection,
    },
    {
      key: 'commission-financial-account',
      header: t(tk.finance.commissionFinancialAccount),
      renderCell: item =>
        props.salesInvoice &&
        getFinancialAccountName(
          props.salesInvoice.saleDate.getFullYear(),
          props.salesInvoice.purchaser.client.internal,
          item,
        ),
    },
    {
      key: 'commission',
      header: t(tk.finance.commission),
      renderCell: (item, sales, commission) =>
        numberFormatter(commission, true),
      isHidden: isGenerated,
      tdCssClass: 'amounts-align-right',
    },
    {
      key: 'commission-amount',
      header: t(tk.common.amount),
      renderCell: (item, sales, commission) => (
        <CurrencyControl
          value={getCommission(item)}
          disabled={
            (commission === 0 && getOriginalCommissionAmount(item) === '0') ||
            props.readOnly
          }
          decimalScale={2}
          onChange={value => onChangeCommission(value, item.id)}
        />
      ),
      isHidden: isCorrection,
    },
    {
      key: 'commission-amount-previous',
      header: t(tk.finance.was),
      renderCell: item => numberFormatter(getPreviousCommission(item), true),
      isHidden: !isCorrection,
      tdCssClass: 'amounts-align-right',
    },
    {
      key: 'commission-copy',
      header: (
        <CopyAllButton
          onClick={() => handleFillAllCommissions?.()}
          tooltip={t(tk.finance.fillAllFieldsWithSales)}
          disabled={props.readOnly}
          allFilled={allCommissionsFilled}
        />
      ),
      renderCell: (item, sales, commission) => (
        <CopySingleButton
          onClick={() => {
            onChangeCommission(
              commission > 0 ? commission.toString() : '0',
              item.id,
            );
          }}
          tooltip={t(tk.finance.fillNotAssignedAmountWithSales)}
          disabled={props.readOnly}
          hidden={
            mathUtils.subtract(getCommission(item), commission) === 0 ||
            (commission < 0 && mathUtils.parse(getCommission(item)) === 0)
          }
        />
      ),
      isHidden: !isCorrection,
    },
    {
      key: 'commission-amount-correction',
      header: t(tk.finance.is),
      renderCell: (item, sales, commission) => (
        <CurrencyControl
          value={getCommission(item)}
          disabled={
            (commission === 0 && getPreviousCommission(item) === '0') ||
            props.readOnly
          }
          decimalScale={2}
          onChange={value => onChangeCommission(value, item.id)}
        />
      ),
      isHidden: !isCorrection,
    },
  ];
};

const GridHead: React.FC<{ columns: TableColumn[] }> = ({ columns }) => (
  <thead>
    <tr>
      {columns
        .filter(({ isHidden }) => !isHidden)
        .map(col => (
          <th key={col.key}>{col.header}</th>
        ))}
    </tr>
  </thead>
);

const GridRow: React.FC<{
  item: GetEstimateItemDto;
  columns: TableColumn[];
  isCorrection: boolean;
  salesInvoiceOriginalEstimateItems?: CreateUpdateInvoiceEstimateItemDto[];
}> = ({ item, columns, salesInvoiceOriginalEstimateItems, isCorrection }) => {
  const sales =
    calculateEstimateItemSalesNet(item, salesInvoiceOriginalEstimateItems) || 0;
  const commission =
    calculateEstimateItemCommission(item, salesInvoiceOriginalEstimateItems) ||
    0;
  const originalSalesAmount =
    salesInvoiceOriginalEstimateItems?.find(
      ({ estimateItemId }) => estimateItemId === item.id,
    )?.amount || '0';
  const originalCommissionAmount =
    salesInvoiceOriginalEstimateItems?.find(
      ({ estimateItemId }) => estimateItemId === item.id,
    )?.commissionAmount || '0';

  if (
    sales === 0 &&
    commission === 0 &&
    !isCorrection &&
    originalSalesAmount === '0' &&
    originalCommissionAmount === '0'
  ) {
    return null;
  }

  return (
    <tr key={item.id} className="align-middle">
      {columns
        .filter(({ isHidden }) => !isHidden)
        .map(col => {
          return (
            <td key={col.key} className={col.tdCssClass}>
              {col.renderCell(item, sales, commission)}
            </td>
          );
        })}
    </tr>
  );
};

export const SalesInvoiceMediaPlanPositionsGrid: React.FC<
  SalesInvoiceMediaplanPositionsGridProps
> = props => {
  const [dateFilter, setDateFilter] = useState<RangeFilter>([null, null]);
  const { t, tk } = useTranslation('fvsLng');
  const isGenerated = [
    SalesInvoiceStatusEnum.Generated,
    SalesInvoiceStatusEnum.Sent,
    SalesInvoiceStatusEnum.Paid,
  ].includes(props.salesInvoice?.status as SalesInvoiceStatusEnum);
  const isCorrection = !!props.salesInvoice?.parent;

  if (!props.availableEstimateItems || !props.salesInvoiceEstimateItems) {
    return <Alert variant="warning">{t(tk.common.noData)}.</Alert>;
  }

  const filteredAvailableEstimateItems = props.availableEstimateItems.filter(
    item =>
      !dateFilter[0] ||
      !dateFilter[1] ||
      moment(item.estimateByMonth?.date).isBetween(
        moment(dateFilter[0]),
        moment(dateFilter[1]),
      ),
  );

  const handleFillAllAmounts = () => {
    const changes = filteredAvailableEstimateItems.map(item => {
      const sales = calculateEstimateItemSalesNet(
        item,
        props.salesInvoiceOriginalEstimateItems,
      );
      return {
        obj: { amount: (sales > 0 ? sales?.toString() : '0') || '' },
        id: item.id,
      };
    });
    props.propertyChangeMultiple?.(changes);
  };

  const handleFillAllCommissions = () => {
    const changes = filteredAvailableEstimateItems.map(item => {
      const commission = calculateEstimateItemCommission(
        item,
        props.salesInvoiceOriginalEstimateItems,
      );
      return {
        obj: {
          commissionAmount:
            (commission > 0 ? commission?.toString() : '0') || '',
        },
        id: item.id,
      };
    });
    props.propertyChangeMultiple?.(changes);
  };

  const allAmountsFilled = filteredAvailableEstimateItems.every(item => {
    const sales = calculateEstimateItemSalesNet(
      item,
      props.salesInvoiceOriginalEstimateItems,
    );
    const currentAmount =
      props.salesInvoiceEstimateItems?.find(
        ({ estimateItemId }) => estimateItemId === item.id,
      )?.amount || '';
    return (
      mathUtils.subtract(currentAmount, sales) === 0 ||
      (sales < 0 && mathUtils.parse(currentAmount) === 0)
    );
  });

  const allCommissionsFilled = filteredAvailableEstimateItems.every(item => {
    const commission = calculateEstimateItemCommission(
      item,
      props.salesInvoiceOriginalEstimateItems,
    );
    const currentCommission =
      props.salesInvoiceEstimateItems?.find(
        ({ estimateItemId }) => estimateItemId === item.id,
      )?.commissionAmount || '';
    return (
      mathUtils.subtract(currentCommission, commission) === 0 ||
      (commission < 0 && mathUtils.parse(currentCommission) === 0)
    );
  });

  const columns = getColumns(
    props,
    isGenerated,
    t,
    tk,
    handleFillAllAmounts,
    handleFillAllCommissions,
    allAmountsFilled,
    allCommissionsFilled,
  );

  return (
    <>
      {props.salesInvoice?.status === SalesInvoiceStatusEnum.Draft &&
        !props.readOnly && (
          <InvoiceEstimateItemsFilters
            estimateItems={props.availableEstimateItems}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
          />
        )}
      <Table striped>
        <GridHead columns={columns} />
        <tbody>
          {filteredAvailableEstimateItems.map(item => (
            <GridRow
              key={item.id}
              item={item}
              salesInvoiceOriginalEstimateItems={
                props.salesInvoiceOriginalEstimateItems
              }
              columns={columns}
              isCorrection={isCorrection}
            />
          ))}
          <SalesInvoiceMediaPlanPositionsSumRow
            {...props}
            filteredAvailableEstimateItems={filteredAvailableEstimateItems}
            isGenerated={isGenerated}
            columns={columns}
          />
        </tbody>
      </Table>
    </>
  );
};
